import {PartnerCodeBox, ReferralSummaryRows} from '@app/checkout/referral-cart-parts';
import {Trans} from '@ui/i18n/trans';
import {Link} from 'react-router';
import {Button} from '@ui/buttons/button';
import {useTrans} from '@ui/i18n/use-trans';
import {message} from '@ui/i18n/message';
import {useState} from 'react';
import {money as fmtMoney} from '@app/student/portal/portal-format';
import {PlaceIcon} from '@ui/icons/material/Place';
import {DepositChoice} from './deposit-choice';
import {
  useApplyCoupon,
  useCart,
  useCartUi,
  useRemoveCartItem,
  useRemoveCoupon,
  useUpdateCartItem,
} from './checkout-queries';

// The shared formatter: "$30" and "$9.99", not "30.00 USD". A cart is the one
// place a price has to read like a price.
const money = (cents: number, currency = 'USD') => fmtMoney(cents / 100, currency);

/**
 * Slide-over cart panel, mounted globally. Reads the server cart; opening is
 * driven by the useCartUi store (set when an item is added).
 *
 * Quantity and coupons are edited HERE rather than only on the checkout page.
 * Both endpoints already existed and nothing called them, so a student who
 * wanted two of something had to add it, go to checkout, change it there, and
 * a coupon could not be tried until they had committed to the checkout flow.
 */
export function CartDrawer() {
  const {isOpen, close} = useCartUi();
  const {data: cart} = useCart();
  const removeItem = useRemoveCartItem();
  const updateItem = useUpdateCartItem();
  const applyCoupon = useApplyCoupon();
  const removeCoupon = useRemoveCoupon();
  const {trans} = useTrans();
  const [code, setCode] = useState('');

  if (!isOpen) return null;
  const items = cart?.items ?? [];
  const currency = cart?.currency || items[0]?.currency || 'USD';

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog">
      <div className="absolute inset-0 bg-black/50" onClick={close} />
      {/* Side panel: capped width on desktop, full-width only on small phones. */}
      <div
        className="relative flex h-full w-full flex-col shadow-2xl"
        style={{backgroundColor: 'var(--be-paper, #fff)', maxWidth: 'min(420px, 100vw)'}}
      >
        <div className="flex items-center justify-between border-b border-divider px-20 py-16">
          <h2 className="text-lg font-bold">
            <Trans message="Your Cart" />
          </h2>
          <button
            className="text-2xl leading-none text-muted hover:text-main"
            onClick={close}
            aria-label={trans(message('Close'))}
          >
            ×
          </button>
        </div>

        <div className="flex-1 space-y-12 overflow-y-auto p-20">
          {items.length === 0 && (
            <p className="py-32 text-center text-sm text-muted">
              <Trans message="Your cart is empty." />
            </p>
          )}

          {items.map(item => (
            <div key={item.id} className="rounded-xl border border-divider p-12">
              <div className="flex items-start gap-10">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold" dir="auto">
                    {item.name}
                  </p>
                  {/* WHICH opportunity. Two lines used to read
                      "Motivation & Recommendation Letters" and
                      "Motivation & Recommendation Letters" with nothing to tell
                      them apart — and a service is bought FOR an opportunity,
                      so the opportunity is half of what was bought. */}
                  {/* The opportunity, marked with a pin rather than labelled
                      "For:" — the icon says the same thing in every language
                      and does not read like a sentence fragment. */}
                  {item.listing_title && !item.tier_id && (
                    <p className="mt-4 flex items-start gap-4 text-xs text-muted" dir="auto">
                      <PlaceIcon size="2xs" className="mt-2 shrink-0" />
                      <span className="min-w-0">{item.listing_title}</span>
                    </p>
                  )}
                  {/* The extras are already inside the price; naming them is
                      the difference between a total and a receipt. */}
                  <DepositChoice item={item} currency={item.currency || currency} />
                  {item.addons?.length > 0 && (
                    <ul className="mt-4 space-y-2">
                      {item.addons.map(addon => (
                        <li key={addon.label} className="flex items-start justify-between gap-8 text-xs">
                          <span className="min-w-0 text-muted" dir="auto">{addon.label}</span>
                          {/* A deduction reads as one: green, with a real minus.
                              "+ I'll do it myself" looked like a charge. */}
                          <span
                            className={`shrink-0 font-semibold ${addon.price < 0 ? 'text-positive' : 'text-primary'}`}
                            dir="ltr"
                          >
                            {addon.price < 0 ? '−' : '+'}
                            {money(Math.abs(addon.price), item.currency || currency)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button
                  className="shrink-0 text-xs text-danger hover:underline"
                  onClick={() => removeItem.mutate(item.id)}
                >
                  <Trans message="Remove" />
                </button>
              </div>

              <div className="mt-10 flex items-center justify-between gap-10">
                {/* No stepper on a line bought in a band — a bigger plan is
                    how you apply to more, not two of the same one. */}
                {item.tier_id ? <span /> : <Stepper
                  quantity={item.quantity}
                  disabled={updateItem.isPending}
                  onChange={quantity => {
                    // One is the floor. Stepping to zero would be a removal
                    // wearing a minus sign, and the student would not know
                    // whether the line had gone or gone wrong.
                    if (quantity < 1) return;
                    updateItem.mutate({itemId: item.id, quantity});
                  }}
                />}
                <span className="text-end">
                  {/* The unit price appears only when there is more than one
                      of something. At quantity 1 it was the same figure twice,
                      one under the other. */}
                  {item.quantity > 1 && (
                    <span className="block text-2xs text-muted" dir="ltr">
                      {money(item.price, item.currency || currency)} × {item.quantity}
                    </span>
                  )}
                  <span className="block text-sm font-bold" dir="ltr">
                    {money(item.price * item.quantity, item.currency || currency)}
                  </span>
                </span>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          // Extra room at the bottom: the support bubble is a fixed element in
          // the same corner this drawer ends in, and it sat on top of the
          // checkout button — the one control the whole panel exists for.
          <div className="space-y-12 border-t border-divider p-20 pb-80">
            {/* Coupon. In the panel rather than only at checkout, because
                "is there a discount?" is a question people ask before they
                decide to check out, not after. */}
            {cart?.coupon_code ? (
              <div className="flex items-center justify-between gap-10 rounded-xl bg-positive/10 px-12 py-8">
                <span className="min-w-0 text-sm font-semibold text-positive">
                  <Trans message="Coupon :code applied" values={{code: cart.coupon_code}} />
                </span>
                <button
                  className="shrink-0 text-xs text-muted hover:text-danger"
                  onClick={() => removeCoupon.mutate()}
                >
                  <Trans message="Remove" />
                </button>
              </div>
            ) : (
              <form
                className="flex gap-8"
                onSubmit={e => {
                  e.preventDefault();
                  const trimmed = code.trim();
                  if (trimmed) applyCoupon.mutate(trimmed, {onSuccess: () => setCode('')});
                }}
              >
                <input
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  placeholder={trans(message('Coupon code'))}
                  className="min-w-0 flex-1 rounded-lg border border-divider bg-paper px-12 py-8 text-sm outline-none focus:border-primary"
                />
                <Button
                  type="submit"
                  variant="outline"
                  size="sm"
                  disabled={!code.trim() || applyCoupon.isPending}
                >
                  <Trans message="Apply" />
                </Button>
              </form>
            )}

            {/* A partner code, here as well as at checkout. */}
            {/* Collapsed to one link once a partner is already applied —
                the code can still be changed. */}
            <PartnerCodeBox collapsible={!!cart!.referral?.partner_id} />

            <div className="space-y-4 text-sm">
              <div className="flex justify-between text-muted">
                <span>
                  <Trans message="Subtotal" />
                </span>
                <span dir="ltr">{money(cart!.subtotal, currency)}</span>
              </div>
              {cart!.discount > 0 && (
                <div className="flex justify-between text-positive">
                  <span>
                    <Trans message="Discount" />
                  </span>
                  <span dir="ltr">−{money(cart!.discount, currency)}</span>
                </div>
              )}
              {/* Same as checkout: the partner discount, why it is there, and
                  a way to take it off (or back). */}
              <ReferralSummaryRows cart={cart!} money={c => money(c, currency)} />
              <div className="flex items-baseline justify-between pt-4 text-base font-bold">
                <span>
                  <Trans message="Total" />
                </span>
                <span className="flex items-baseline gap-8">
                  {(cart!.referral_discount ?? 0) > 0 && (
                    <span className="text-sm font-medium text-muted line-through" dir="ltr">
                      {money(cart!.total + cart!.referral_discount!, currency)}
                    </span>
                  )}
                  <span dir="ltr" className={(cart!.referral_discount ?? 0) > 0 ? 'text-positive' : ''}>
                    {money(cart!.total, currency)}
                  </span>
                </span>
              </div>
              {cart!.due_later > 0 && (
                <div className="flex justify-between text-sm font-semibold text-primary">
                  <span>
                    <Trans message="Due now" />
                  </span>
                  <span dir="ltr">{money(cart!.due_now, currency)}</span>
                </div>
              )}
            </div>

            <Button
              variant="flat"
              color="primary"
              className="w-full rounded-full"
              elementType={Link}
              to="/checkout"
              onClick={close}
            >
              <Trans message="Checkout" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * − n + for one line's quantity.
 *
 * Laid out with flex rather than absolute positions so it mirrors in Arabic
 * without a second set of rules: minus stays on the reading edge either way.
 */
function Stepper({
  quantity,
  disabled,
  onChange,
}: {
  quantity: number;
  disabled?: boolean;
  onChange: (quantity: number) => void;
}) {
  const btn =
    'flex h-26 w-26 items-center justify-center rounded-lg border border-divider text-sm font-bold transition hover:border-primary hover:text-primary disabled:opacity-40';

  return (
    <div className="flex items-center gap-8">
      <button
        type="button"
        className={btn}
        disabled={disabled || quantity <= 1}
        aria-label="Decrease quantity"
        onClick={() => onChange(quantity - 1)}
      >
        −
      </button>
      <span className="min-w-24 text-center text-sm font-semibold" dir="ltr">
        {quantity}
      </span>
      <button
        type="button"
        className={btn}
        disabled={disabled}
        aria-label="Increase quantity"
        onClick={() => onChange(quantity + 1)}
      >
        +
      </button>
    </div>
  );
}
